import { CheckCircleOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { Table, TableColumnsType, Typography } from 'antd'

import { AppAdmin } from 'api/app-admin'
import LinkAddress from 'component/link/link-address'
import useSearchParams from 'hooks/use-search-params'
import { formatDate } from 'utils/format-time'
import { logInfo } from 'utils/logger'

const { Text } = Typography

interface WinnerReward {
  _id: string
  contractAddress: string
  tokenIds: { tokenId: number; createdAt: string }[]
  rarityId: number
  minter: string
  txHash: string
  claimStatus: number
  claimedAt: string | null
  createdAt: string
  goldenTicket: {
    name: string
    description: string
    image: string
  }
  tokenize: {
    fullName: string
    thumbnail: string
  }
  reward: {
    rarity: number
    rewardAmount: number
    availableReward: number
    asset: string
    activationDate: string
    contractAddress: string
  }
  account: {
    firstName: string
    lastName: string
    email: string
    phone: string
    avatar: string
    isKYC: boolean
  }
}

const fetchWinnerRewardsPool = async (searchParams: Record<string, any>) => {
  const response = await AppAdmin.getListWinnerRewardsPool(searchParams)
  logInfo(response.data.data)
  if (response.data?.data) {
    return {
      winners: response.data.data.results as WinnerReward[],
      total: response.data.data.totalResults,
    }
  }
  return { winners: [], total: 0 }
}

const columns: TableColumnsType<WinnerReward> = [
  {
    title: 'PTO',
    dataIndex: ['tokenize', 'fullName'],
    key: 'pto',
    render: (value) => <Text style={{ maxWidth: 100 }}>{value || '-'}</Text>,
  },
  {
    title: 'Ticket Program',
    dataIndex: ['goldenTicket', 'name'],
    key: 'ticket',
  },
  {
    title: 'Contract Address',
    dataIndex: 'contractAddress',
    key: 'contractAddress',
    render: (value) => (value ? <LinkAddress address={value} type="address" max={4} /> : '-'),
  },
  {
    title: 'Wallet Address',
    dataIndex: 'minter',
    key: 'minter',
    align: 'center',
    render: (address) => (address ? <LinkAddress address={address} type="address" max={4} /> : '-'),
  },
  {
    title: 'Email',
    dataIndex: ['account', 'email'],
    key: 'email',
  },
  {
    title: 'Reward Amount',
    dataIndex: ['reward', 'rewardAmount'],
    key: 'rewardAmount',
    render: (am, record) => am * record.tokenIds.length || '-',
  },
  {
    title: 'Asset',
    dataIndex: ['reward', 'asset'],
    key: 'asset',
    align: 'center',
    render: (asset) => (asset ? <span className="winner-rewards__asset">{asset}</span> : 'N/A'),
  },
  {
    title: 'Claimed at',
    dataIndex: 'claimedAt',
    key: 'claimedAt',
    render: (date) => (date ? formatDate(date) : 'N/A'),
  },
  {
    title: 'Status',
    dataIndex: 'claimStatus',
    key: 'claimStatus',
    render: (status) => {
      const isClaimed = status === 1
      return (
        <div
          className={`winner-rewards__status ${isClaimed ? 'status-success' : 'status-pending'}`}
        >
          <CheckCircleOutlined />
          {isClaimed ? 'claimed' : 'unclaimed'}
        </div>
      )
    },
  },
]

export const PTOWinnerRewardsTable = () => {
  const { searchParams, initPagination, onChangePagination } = useSearchParams()

  const { data, isLoading } = useQuery({
    queryKey: ['winnerRewardsPool', searchParams],
    queryFn: () => fetchWinnerRewardsPool(searchParams),
    gcTime: 30000,
  })

  const winners = data?.winners ?? []
  const total = data?.total ?? 0

  return (
    <Table
      columns={columns}
      loading={isLoading}
      dataSource={winners}
      rowKey={(record) => record._id}
      pagination={initPagination(total)}
      onChange={onChangePagination}
      scroll={{ x: 1000 }}
      bordered
    />
  )
}
